// src/pages/admin/AdminVerify.jsx
// git commit: "fix(AdminVerify): retry verify click on network/5xx errors to survive brief restart gaps"

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck } from "lucide-react";

// Same retry pattern as AdminLogin — covers the brief restart gap.
// Does NOT retry on 401 (expired/invalid token is a real, final answer).
async function getWithRetry(url, maxRetries = 3) {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await axios.get(url);
      return res;
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      const shouldRetry = !err.response || status >= 500 || status === 503;

      if (!shouldRetry || attempt === maxRetries) {
        throw err;
      }

      await new Promise((r) => setTimeout(r, 1200 * attempt));
      console.log(`[AdminVerify] Retrying attempt ${attempt + 1}...`);
    }
  }
  throw lastError;
}

export default function AdminVerify() {
  const navigate            = useNavigate();
  const [searchParams]      = useSearchParams();
  const [status, setStatus] = useState("ready"); // "ready" | "verifying" | "success" | "error"
  const [message, setMessage] = useState("");

  const token = searchParams.get("token");

  const handleVerify = async () => {
    if (!token) {
      setStatus("error");
      setMessage("No token found in the link. Please request a new login link.");
      return;
    }

    setStatus("verifying");

    try {
      const res = await getWithRetry(
        `https://api.larrydigitals.com/api/admin/verify?token=${token}`
      );

      if (res.data?.token) {
        localStorage.setItem("adminToken", res.data.token);
        setStatus("success");
        setTimeout(() => navigate("/admin/dashboard"), 1200);
      } else {
        throw new Error("No token in response");
      }
    } catch (err) {
      setStatus("error");
      const msg = err.response?.data?.message || "Verification failed. The link may have expired.";
      setMessage(msg);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-[420px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-8 text-center">

        {status === "ready" && (
          <>
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <ShieldCheck size={32} className="text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Confirm Sign In
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Click below to securely sign in to the admin panel.
            </p>
            <button
              onClick={handleVerify}
              disabled={!token}
              className="w-full h-11 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition"
            >
              Sign In
            </button>
            {!token && (
              <p className="text-xs text-red-500 mt-3">
                No token found in the link. Please request a new login link.
              </p>
            )}
          </>
        )}

        {status === "verifying" && (
          <>
            <div className="flex justify-center mb-5">
              <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Verifying your link...
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Please wait while we securely sign you in.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Login successful!
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Redirecting you to the dashboard...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Link Invalid or Expired
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {message}
            </p>
            <button
              onClick={() => navigate("/admin/login")}
              className="inline-block px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}